import * as Sentry from "@sentry/vue";

// Decide whether an axios failure is worth telling Sentry about.
//
// Every API call in the dashboard funnels through the single shared instance in
// ./request.js, so this is the one place that judgement lives. Two cases are
// deliberately NOT reported:
//
//   - `config.silent` — speculative prefetches (MapView's prefetchCountry,
//     Leaderboard's page-0 warm-up). The user may never open that view; a
//     failure costs nothing and carries no user impact.
//   - 404 — a stale team or slug URL is normal user behaviour, not a fault.
//     Home.vue's slug lookups reject on 404 by design (see request.js's
//     validateStatus) and handle it with a not-found state.
//
// Everything else reports: no response at all (network down, timeout) and 5xx
// as errors, other 4xx as warnings.
export const shouldReport = (error) => {
  const status = error && error.response && error.response.status;
  if (error && error.config && error.config.silent) {
    return {
      report: false,
      level: status && status < 500 ? "warning" : "error",
    };
  }
  if (!status) return { report: true, level: "error" };
  if (status >= 500) return { report: true, level: "error" };
  if (status === 404) return { report: false, level: "warning" };
  return { report: true, level: "warning" };
};

// Path only — never the query string. TeamFilter puts the user's typed search
// text in params, and that must not leave the browser as error metadata.
const pathOf = (error) => {
  const url = (error && error.config && error.config.url) || "unknown";
  return String(url).split("?")[0];
};

export const reportApiError = (error) => {
  const { report, level } = shouldReport(error);
  if (!report) return;
  const status = error && error.response && error.response.status;
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag("api.path", pathOf(error));
    scope.setTag("api.status", status ? String(status) : "network");
    Sentry.captureException(error);
  });
};

// The Geocoder fallbacks in MapView are correct — the map keeps working — but a
// persistent failure (Geocoding API disabled, key restricted) would otherwise be
// completely invisible. Always reported, at warning level.
export const reportGeocoderError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("geocoder.operation", context.operation || "unknown");
    Sentry.captureException(error);
  });
};
